import { z } from 'zod'

/**
 * Learning activity contract — layers 2 and 3 of the three-layer model
 * (D-11): the client delivery DTO and the response & assessment contract.
 * Layer 1, the server-side content authoring schema, is specified in
 * `docs/design/content-authoring-schema-v0.1.md` and is not implemented here.
 *
 * Sources:
 *   - Requirements v1.5 FR-40, FR-42, FR-43, FR-45, FR-50, FR-71, FR-74–FR-77,
 *     FR-143, FR-154 (the nine P0 `activity_type` values) and SEC-D;
 *   - Exercise Catalog v1.0 §4 (families, variants, response shapes);
 *   - Activity System v0.1 §3–§6 and Backend Contracts v0.1 §4.4–§4.7.
 *
 * Domain-neutral: competency and level codes are opaque strings owned by the
 * domain module. Delivery schemas are strict objects, so a payload carrying an
 * answer key or any other unexpected field is rejected at the boundary
 * (SEC-D, Activity System §6) instead of silently reaching the UI.
 *
 * Validation layers (none of them is scoring):
 *   - schema parse: shape, strictness and per-object invariants;
 *   - `responseProblems(item, response)`: does a response fit the delivered
 *     item? The client may use it for usability; the backend repeats it and
 *     is authoritative, because client responses are untrusted;
 *   - `summaryProblems(delivery, summary)`: does a summary fit its session?
 * Correctness comes only from the assessment service (FR-50).
 *
 * STATUS (D-11, docs/decisions/d-11-activity-contracts.md): client delivery
 * and response architecture approved by the product owner, with these
 * decisions applied:
 *   - read aloud is assessed per sentence segment; partial readings are
 *     accepted and missing sentences are never errors;
 *   - `is_correct` is true only when `score === max_score`; partial credit
 *     exists only for matching and gap fill and is computed by the backend;
 *   - a failed read-aloud segment may report `low` audio when that was
 *     determined, otherwise null.
 * Assumptions still in force:
 *   - listening and reading items carry one closed question each; reading
 *     items that share a `passage_id` carry the same passage;
 *   - picture choice is single-select; true/false is a `text_choice` variant;
 *   - option/token/gap/entry IDs are short opaque strings, items and sessions
 *     are UUIDs;
 *   - each item is submitted once. Retrying after a wrong answer (FR-161,
 *     `max_attempts`) is undecided and not modelled.
 */

export const ACTIVITY_SCHEMA_VERSION = 1

/** Requirements v1.5 FR-154: the P0 minimum, in the order listed there. */
export const ACTIVITY_TYPES = [
  'text_choice',
  'picture_choice',
  'matching',
  'word_order',
  'gap_fill',
  'listen_respond',
  'read_and_answer',
  'read_aloud',
  'speaking_prompt',
] as const
export const activityTypeSchema = z.enum(ACTIVITY_TYPES)
export type ActivityType = z.infer<typeof activityTypeSchema>

/** FR-38. Decides feedback timing (FR-42). */
export const DATA_CLASSES = [
  'practice',
  'self_practice',
  'diagnostic',
  'assessment',
] as const
export const dataClassSchema = z.enum(DATA_CLASSES)
export type DataClass = z.infer<typeof dataClassSchema>

/** FR-42: practice gets feedback after each item; diagnostic/assessment at the end. */
export function feedbackIsImmediate(dataClass: DataClass): boolean {
  return dataClass === 'practice' || dataClass === 'self_practice'
}

/** FR-43: listening audio plays at most twice. */
export const LISTENING_MAX_PLAYS = 2

/** FR-71: one read-aloud segment is at most 30 seconds. */
export const READ_ALOUD_MAX_SEGMENT_MS = 30_000

/** FR-75: `max_flagged_words` is an integer 0–5. */
export const MAX_FLAGGED_WORDS_LIMIT = 5

const id = z.string().min(1).max(64)
const text = z.string().min(1)

function hasDuplicates(values: readonly unknown[]): boolean {
  return new Set(values).size !== values.length
}

function sameMembers(values: readonly string[], expected: readonly string[]): boolean {
  return (
    values.length === expected.length &&
    !hasDuplicates(values) &&
    values.every((v) => expected.includes(v))
  )
}

// ---------------------------------------------------------------------------
// Shared delivery parts
// ---------------------------------------------------------------------------

/** FR-45: simple English instruction plus Vietnamese help behind a "?" button. */
export const instructionSchema = z.strictObject({
  en: text,
  vi: text,
})
export type Instruction = z.infer<typeof instructionSchema>

export const learningObjectiveSchema = z.strictObject({
  competency_code: text,
  skill: text,
  level_code: text,
})
export type LearningObjective = z.infer<typeof learningObjectiveSchema>

/**
 * Media reference. `url` is null when no playable/viewable file exists (for
 * example a mock placeholder); the renderer must then show its fallback
 * (FR-144). `is_mock` marks demonstration assets that must carry a visible
 * MOCK label (decision D-05).
 */
export const assetRefSchema = z.strictObject({
  asset_id: id,
  kind: z.enum(['image', 'audio']),
  url: z.url().nullable(),
  alt_text: text,
  is_mock: z.boolean(),
})
export type AssetRef = z.infer<typeof assetRefSchema>

export const textOptionSchema = z.strictObject({
  option_id: id,
  text,
})
export type TextOption = z.infer<typeof textOptionSchema>

const uniqueOptions = (options: readonly { option_id: string }[]) =>
  !hasDuplicates(options.map((o) => o.option_id))

const UNIQUE_OPTIONS = { message: 'option IDs must be unique within an item' }

/** A closed question inside a listening or reading item (Catalog §4.5–§4.6). */
export const closedQuestionSchema = z.discriminatedUnion('answer_mode', [
  z
    .strictObject({
      answer_mode: z.literal('choice'),
      prompt: text,
      options: z.array(textOptionSchema).min(2),
    })
    .refine((q) => uniqueOptions(q.options), UNIQUE_OPTIONS),
  z.strictObject({
    answer_mode: z.literal('text'),
    prompt: text,
  }),
])
export type ClosedQuestion = z.infer<typeof closedQuestionSchema>

const itemBase = {
  /**
   * The immutable item version shown to the learner: `content_item_versions.id`
   * (DB v0.2 §4.3, DB-03), referenced server-side as `item_version_id`. Every
   * response and score traces to it (TECH-07).
   */
  item_id: z.uuid(),
  schema_version: z.literal(ACTIVITY_SCHEMA_VERSION),
  title: text,
  instruction: instructionSchema,
  learning_objective: learningObjectiveSchema,
  estimated_seconds: z.number().int().positive(),
}

// ---------------------------------------------------------------------------
// Activity items (client-safe delivery, one per activity type)
// ---------------------------------------------------------------------------

/** Catalog §4.1: single, multiple (only when authored) or true/false. */
export const textChoiceItemSchema = z
  .strictObject({
    ...itemBase,
    activity_type: z.literal('text_choice'),
    variant: z.enum(['single', 'multiple', 'true_false']),
    prompt: text,
    context: text.nullable(),
    options: z.array(textOptionSchema).min(2),
    min_selections: z.number().int().min(1),
    max_selections: z.number().int().min(1),
  })
  .refine((item) => uniqueOptions(item.options), UNIQUE_OPTIONS)
  .refine((item) => item.min_selections <= item.max_selections, {
    message: 'min_selections must not exceed max_selections',
  })
  .refine((item) => item.max_selections <= item.options.length, {
    message: 'max_selections must not exceed the number of options',
  })
  .refine((item) => item.variant === 'multiple' || item.max_selections === 1, {
    message: 'single and true_false items take exactly one selection',
  })
  .refine((item) => item.variant !== 'true_false' || item.options.length === 2, {
    message: 'true_false items have exactly two options',
  })

export const pictureOptionSchema = z.strictObject({
  option_id: id,
  image: assetRefSchema,
})

/** Single-select (D-11 assumption). */
export const pictureChoiceItemSchema = z
  .strictObject({
    ...itemBase,
    activity_type: z.literal('picture_choice'),
    prompt: text,
    prompt_audio: assetRefSchema.nullable(),
    options: z.array(pictureOptionSchema).min(2),
  })
  .refine((item) => uniqueOptions(item.options), UNIQUE_OPTIONS)

/** A matching entry shows text, an image, or both — never neither. */
export const matchEntrySchema = z
  .strictObject({
    entry_id: id,
    text: text.nullable(),
    image: assetRefSchema.nullable(),
  })
  .refine((e) => e.text !== null || e.image !== null, {
    message: 'a matching entry needs text or an image',
  })

/**
 * Catalog §4.2. Every left entry must be matched. `allow_reuse` false means
 * each right entry is used at most once, so there must be at least as many
 * right entries as left ones; extra right entries act as distractors.
 */
export const matchingItemSchema = z
  .strictObject({
    ...itemBase,
    activity_type: z.literal('matching'),
    left: z.array(matchEntrySchema).min(2),
    right: z.array(matchEntrySchema).min(2),
    allow_reuse: z.boolean(),
  })
  .refine((item) => !hasDuplicates(item.left.map((e) => e.entry_id)), {
    message: 'left entry IDs must be unique',
  })
  .refine((item) => !hasDuplicates(item.right.map((e) => e.entry_id)), {
    message: 'right entry IDs must be unique',
  })
  .refine((item) => item.allow_reuse || item.right.length >= item.left.length, {
    message: 'without reuse, there must be at least as many right entries as left',
  })

export const gapSegmentSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('text'), text }),
  z.strictObject({ type: z.literal('gap'), gap_id: id }),
])

/** Gap IDs of a gap-fill item, in reading order. */
export function gapIdsOf(item: {
  segments: readonly z.infer<typeof gapSegmentSchema>[]
}) {
  return item.segments.flatMap((s) => (s.type === 'gap' ? [s.gap_id] : []))
}

/** Catalog §4.3: word bank or free text input, with indexed gaps. */
export const gapFillItemSchema = z
  .strictObject({
    ...itemBase,
    activity_type: z.literal('gap_fill'),
    input_mode: z.enum(['word_bank', 'free_text']),
    segments: z.array(gapSegmentSchema).min(1),
    word_bank: z.array(textOptionSchema).nullable(),
  })
  .refine((item) => gapIdsOf(item).length > 0, {
    message: 'gap_fill items need at least one gap',
  })
  .refine((item) => !hasDuplicates(gapIdsOf(item)), {
    message: 'gap IDs must be unique within an item',
  })
  .refine((item) => (item.input_mode === 'word_bank') === (item.word_bank !== null), {
    message: 'word_bank is required for word_bank input and absent otherwise',
  })
  .refine((item) => item.word_bank === null || item.word_bank.length > 0, {
    message: 'a word bank needs at least one word',
  })
  .refine(
    (item) => item.word_bank === null || uniqueOptions(item.word_bank),
    UNIQUE_OPTIONS,
  )

/**
 * Catalog §4.4. Tokens are delivered in presentation (shuffled) order and the
 * learner arranges all of them. Fixed (pre-placed) tokens from the catalog are
 * not supported in R1; they are a future extension (D-11).
 */
export const wordOrderItemSchema = z
  .strictObject({
    ...itemBase,
    activity_type: z.literal('word_order'),
    prompt: text.nullable(),
    tokens: z.array(z.strictObject({ token_id: id, text })).min(2),
  })
  .refine((item) => !hasDuplicates(item.tokens.map((t) => t.token_id)), {
    message: 'token IDs must be unique within an item',
  })

/** Catalog §4.5. Transcript and answer key are never delivered before submission. */
export const listenRespondItemSchema = z.strictObject({
  ...itemBase,
  activity_type: z.literal('listen_respond'),
  audio: assetRefSchema,
  max_plays: z.literal(LISTENING_MAX_PLAYS),
  question: closedQuestionSchema,
})

export const passageSchema = z.strictObject({
  passage_id: id,
  title: text.nullable(),
  paragraphs: z.array(text).min(1),
})

/**
 * Catalog §4.6. One question per item; consecutive items may share a
 * passage (same `passage_id`) so feedback can follow each question (FR-42).
 */
export const readAndAnswerItemSchema = z.strictObject({
  ...itemBase,
  activity_type: z.literal('read_and_answer'),
  passage: passageSchema,
  question: closedQuestionSchema,
})

/**
 * FR-70: the passage is split into sentences, each recorded and assessed as
 * one segment. A sentence `sequence` is the authored position within this
 * immutable item version (`item_id`); it is stable only together with
 * `item_id`, not across versions. How passages are stored server-side is open
 * decision D-4; this is only the delivered shape.
 */
export const readAloudItemSchema = z
  .strictObject({
    ...itemBase,
    activity_type: z.literal('read_aloud'),
    passage_id: id,
    sentences: z
      .array(z.strictObject({ sequence: z.number().int().min(1), text }))
      .min(1),
    max_segment_ms: z.literal(READ_ALOUD_MAX_SEGMENT_MS),
  })
  .refine((item) => !hasDuplicates(item.sentences.map((s) => s.sequence)), {
    message: 'sentence sequence numbers must be unique',
  })

/**
 * Speaking prompt, decision D-03. R1: the learner responds aloud and the task
 * is recorded as completed — no recording, no AI score, no correctness, no
 * parent checklist. The parent checklist (FR-85/FR-86) is an R2 feature and is
 * deliberately not modelled here.
 */
export const speakingPromptItemSchema = z.strictObject({
  ...itemBase,
  activity_type: z.literal('speaking_prompt'),
  prompt: text,
  visual_cue: assetRefSchema.nullable(),
})

export const activityItemSchema = z.discriminatedUnion('activity_type', [
  textChoiceItemSchema,
  pictureChoiceItemSchema,
  matchingItemSchema,
  wordOrderItemSchema,
  gapFillItemSchema,
  listenRespondItemSchema,
  readAndAnswerItemSchema,
  readAloudItemSchema,
  speakingPromptItemSchema,
])
export type ActivityItem = z.infer<typeof activityItemSchema>
export type ActivityItemOf<T extends ActivityType> = Extract<
  ActivityItem,
  { activity_type: T }
>

// ---------------------------------------------------------------------------
// Lessons and sessions
// ---------------------------------------------------------------------------

/** What Child Home needs for the "Bài học hôm nay" card (UI v0.2 §4.2). */
export const lessonSummarySchema = z.strictObject({
  lesson_id: z.uuid(),
  title: text,
  /** Distinct types actually included in the lesson, in lesson order. */
  activity_types: z.array(activityTypeSchema).min(1),
  item_count: z.number().int().min(1),
  estimated_seconds: z.number().int().positive(),
  /** Demonstration content that must be labelled MOCK (decision D-02/D-05). */
  is_mock: z.boolean(),
  /**
   * The lesson content has not had teacher review or content validation
   * (first-learning-journey §6). It says nothing about grading: a real
   * attempt at sample content is still real, recorded learner activity.
   */
  is_sample_content: z.boolean(),
})
export type LessonSummary = z.infer<typeof lessonSummarySchema>

/** Reading items that reuse a `passage_id` must carry an identical passage. */
function passagesConsistent(items: readonly ActivityItem[]): boolean {
  const seen = new Map<string, string>()
  for (const item of items) {
    if (item.activity_type !== 'read_and_answer') continue
    const body = JSON.stringify(item.passage)
    const previous = seen.get(item.passage.passage_id)
    if (previous !== undefined && previous !== body) return false
    seen.set(item.passage.passage_id, body)
  }
  return true
}

export const sessionDeliverySchema = z
  .strictObject({
    session_id: z.uuid(),
    lesson_id: z.uuid(),
    learner_id: z.uuid(),
    data_class: dataClassSchema,
    is_mock: z.boolean(),
    /** As on `LessonSummary`: unreviewed content, not simulated results. */
    is_sample_content: z.boolean(),
    items: z.array(activityItemSchema).min(1),
  })
  .refine((s) => !hasDuplicates(s.items.map((i) => i.item_id)), {
    message: 'item IDs must be unique within a session',
  })
  .refine((s) => passagesConsistent(s.items), {
    message: 'reading items sharing a passage_id must carry the same passage',
  })
export type SessionDelivery = z.infer<typeof sessionDeliverySchema>

// ---------------------------------------------------------------------------
// Response payloads (client → assessment service)
// ---------------------------------------------------------------------------

const selectionSchema = z.strictObject({
  selected_option_ids: z
    .array(id)
    .min(1)
    .refine((ids) => !hasDuplicates(ids), { message: 'duplicate option ID' }),
})

export const closedAnswerSchema = z.union([
  selectionSchema,
  z.strictObject({ text: z.string().max(200) }),
])
export type ClosedAnswer = z.infer<typeof closedAnswerSchema>

export const gapAnswerSchema = z.union([
  z.strictObject({ gap_id: id, text: z.string().max(200) }),
  z.strictObject({ gap_id: id, option_id: id }),
])

export const responsePayloadSchema = z.discriminatedUnion('activity_type', [
  z.strictObject({ activity_type: z.literal('text_choice'), ...selectionSchema.shape }),
  z.strictObject({
    activity_type: z.literal('picture_choice'),
    ...selectionSchema.shape,
  }),
  z
    .strictObject({
      activity_type: z.literal('matching'),
      pairs: z.array(z.strictObject({ left_id: id, right_id: id })).min(1),
    })
    .refine((r) => !hasDuplicates(r.pairs.map((p) => p.left_id)), {
      message: 'each left entry may be matched only once',
    }),
  z
    .strictObject({
      activity_type: z.literal('word_order'),
      token_ids: z.array(id).min(1),
    })
    .refine((r) => !hasDuplicates(r.token_ids), { message: 'duplicate token ID' }),
  z
    .strictObject({
      activity_type: z.literal('gap_fill'),
      gaps: z.array(gapAnswerSchema).min(1),
    })
    .refine((r) => !hasDuplicates(r.gaps.map((g) => g.gap_id)), {
      message: 'each gap may be answered only once',
    }),
  z.strictObject({
    activity_type: z.literal('listen_respond'),
    /** Recorded as evidence (Catalog §4.5); never above FR-43's limit. */
    play_count: z.number().int().min(0).max(LISTENING_MAX_PLAYS),
    answer: closedAnswerSchema,
  }),
  z.strictObject({
    activity_type: z.literal('read_and_answer'),
    answer: closedAnswerSchema,
  }),
  z
    .strictObject({
      activity_type: z.literal('read_aloud'),
      segments: z
        .array(
          z.strictObject({
            sequence: z.number().int().min(1),
            recording_id: id,
            duration_ms: z.number().int().positive().max(READ_ALOUD_MAX_SEGMENT_MS),
          }),
        )
        .min(1),
    })
    .refine((r) => !hasDuplicates(r.segments.map((s) => s.sequence)), {
      message: 'segment sequence numbers must be unique',
    })
    .refine((r) => !hasDuplicates(r.segments.map((s) => s.recording_id)), {
      message: 'recording IDs must be unique',
    }),
  /** D-03: completion only. No recording, score or checklist fields. */
  z.strictObject({
    activity_type: z.literal('speaking_prompt'),
    completed: z.literal(true),
  }),
])
export type ResponsePayload = z.infer<typeof responsePayloadSchema>
export type ResponsePayloadOf<T extends ActivityType> = Extract<
  ResponsePayload,
  { activity_type: T }
>

export const submitItemInputSchema = z.strictObject({
  session_id: z.uuid(),
  item_id: z.uuid(),
  /** Idempotency key (FR-41): one per item submission. */
  client_attempt_id: z.uuid(),
  response: responsePayloadSchema,
  elapsed_ms: z.number().int().min(0),
  hinted: z.boolean(),
})
export type SubmitItemInput = z.infer<typeof submitItemInputSchema>

function closedAnswerProblems(question: ClosedQuestion, answer: ClosedAnswer): string[] {
  if (question.answer_mode === 'choice') {
    if (!('selected_option_ids' in answer))
      return ['choice question needs a selected option']
    const options = question.options.map((o) => o.option_id)
    const selected = answer.selected_option_ids
    if (selected.length !== 1) return ['choose exactly one option']
    return selected.every((o) => options.includes(o))
      ? []
      : ['option is not in this question']
  }
  if (!('text' in answer)) return ['text question needs a text answer']
  return answer.text.trim() === '' ? ['answer is empty'] : []
}

/**
 * Does `response` fit the delivered `item`: right activity type, IDs that
 * belong to the item, no duplicates and a complete answer? Returns a list of
 * problems (empty when valid). This is structural validation, not scoring.
 * The backend must run the same checks; the client cannot be trusted.
 */
export function responseProblems(
  item: ActivityItem,
  response: ResponsePayload,
): string[] {
  const parsed = responsePayloadSchema.safeParse(response)
  if (!parsed.success) return parsed.error.issues.map((i) => i.message)
  if (response.activity_type !== item.activity_type) {
    return [`response is for ${response.activity_type}, item is ${item.activity_type}`]
  }

  switch (item.activity_type) {
    case 'text_choice':
    case 'picture_choice': {
      const selected = (response as ResponsePayloadOf<'text_choice'>).selected_option_ids
      const options = item.options.map((o) => o.option_id)
      const [min, max] =
        item.activity_type === 'text_choice'
          ? [item.min_selections, item.max_selections]
          : [1, 1]
      const problems: string[] = []
      if (!selected.every((o) => options.includes(o)))
        problems.push('option is not in this item')
      if (selected.length < min || selected.length > max) {
        problems.push(`select between ${String(min)} and ${String(max)} options`)
      }
      return problems
    }
    case 'matching': {
      const { pairs } = response as ResponsePayloadOf<'matching'>
      const left = item.left.map((e) => e.entry_id)
      const right = item.right.map((e) => e.entry_id)
      const targets = pairs.map((p) => p.right_id)
      const problems: string[] = []
      if (
        !sameMembers(
          pairs.map((p) => p.left_id),
          left,
        )
      ) {
        problems.push('every left entry must be matched exactly once')
      }
      if (!targets.every((t) => right.includes(t)))
        problems.push('right entry is not in this item')
      if (!item.allow_reuse && hasDuplicates(targets))
        problems.push('right entry used twice')
      return problems
    }
    case 'word_order': {
      const { token_ids } = response as ResponsePayloadOf<'word_order'>
      const tokens = item.tokens.map((t) => t.token_id)
      return sameMembers(token_ids, tokens) ? [] : ['arrange every token exactly once']
    }
    case 'gap_fill': {
      const { gaps } = response as ResponsePayloadOf<'gap_fill'>
      const bank = item.word_bank?.map((o) => o.option_id) ?? []
      const problems: string[] = []
      if (
        !sameMembers(
          gaps.map((g) => g.gap_id),
          gapIdsOf(item),
        )
      ) {
        problems.push('answer every gap exactly once')
      }
      for (const gap of gaps) {
        if (item.input_mode === 'word_bank') {
          if (!('option_id' in gap)) problems.push('choose a word from the word bank')
          else if (!bank.includes(gap.option_id))
            problems.push('word is not in the word bank')
        } else if (!('text' in gap)) {
          problems.push('type the missing word')
        } else if (gap.text.trim() === '') {
          problems.push('gap is empty')
        }
      }
      return problems
    }
    case 'listen_respond':
      return closedAnswerProblems(
        item.question,
        (response as ResponsePayloadOf<'listen_respond'>).answer,
      )
    case 'read_and_answer':
      return closedAnswerProblems(
        item.question,
        (response as ResponsePayloadOf<'read_and_answer'>).answer,
      )
    case 'read_aloud': {
      // Partial readings are valid (FR-77): any non-empty subset of the
      // delivered sentences. Sentences without a segment stay unassessed.
      const { segments } = response as ResponsePayloadOf<'read_aloud'>
      const delivered = item.sentences.map((s) => s.sequence)
      return segments.every((s) => delivered.includes(s.sequence))
        ? []
        : ['segment is not one of the delivered sentences']
    }
    case 'speaking_prompt':
      return []
  }
}

// ---------------------------------------------------------------------------
// Read aloud assessment (FR-72–FR-79), one result per sentence segment
// ---------------------------------------------------------------------------

export const ASSESSMENT_STATUSES = [
  'pending',
  'assessed',
  'no_speech',
  'failed_retryable',
  'failed',
] as const
export const assessmentStatusSchema = z.enum(ASSESSMENT_STATUSES)
export type AssessmentStatus = z.infer<typeof assessmentStatusSchema>

/** FR-74: `ok` | `low` | null (null = quality could not be determined). */
export const audioQualitySchema = z.enum(['ok', 'low']).nullable()
export type AudioQuality = z.infer<typeof audioQualitySchema>

/** FR-75 runtime configuration (server-side). */
export const readAloudConfigSchema = z.strictObject({
  low_accuracy_threshold: z.number().min(0).max(100),
  max_flagged_words: z.number().int().min(0).max(MAX_FLAGGED_WORDS_LIMIT),
})
export type ReadAloudConfig = z.infer<typeof readAloudConfigSchema>
export const READ_ALOUD_DEFAULT_CONFIG: ReadAloudConfig = {
  low_accuracy_threshold: 40,
  max_flagged_words: 5,
}

interface ReadAloudStatusFields {
  assessment_status: AssessmentStatus
  audio_quality: AudioQuality
  word_flags: readonly unknown[]
}

/**
 * FR-74 consistency for one segment (product-owner decisions, D-11):
 *   - `pending` and `failed_retryable`: quality not (yet) determined → null;
 *   - `failed`: `low` when the audio was determined to be too poor, null when
 *     a service failure prevented any judgement — never `ok`, and a service
 *     failure is never reported as `low`;
 *   - `no_speech`: no speech evidence → `low`;
 *   - `assessed`: `ok` or `low`;
 *   - word flags only for `assessed` with `ok` audio (FR-74, FR-76).
 */
function readAloudStatusIssues(r: ReadAloudStatusFields, ctx: z.RefinementCtx): void {
  const issue = (message: string) => {
    ctx.addIssue({ code: 'custom', message })
  }
  switch (r.assessment_status) {
    case 'pending':
    case 'failed_retryable':
      if (r.audio_quality !== null) {
        issue(`audio_quality must be null while ${r.assessment_status}`)
      }
      break
    case 'failed':
      if (r.audio_quality === 'ok') issue('a failed assessment cannot have ok audio')
      break
    case 'no_speech':
      if (r.audio_quality !== 'low') issue('no_speech implies audio_quality low')
      break
    case 'assessed':
      if (r.audio_quality === null) issue('an assessed segment has an audio quality')
      break
  }
  const flagsAllowed = r.assessment_status === 'assessed' && r.audio_quality === 'ok'
  if (!flagsAllowed && r.word_flags.length > 0) {
    issue('word flags are only created for assessed segments with ok audio')
  }
}

const segmentSequence = z.number().int().min(1)

/** FR-74: one stored word flag. `accuracy` is Azure's word-level AccuracyScore. */
export const wordFlagSchema = z.strictObject({
  word: text,
  accuracy: z.number().min(0).max(100),
  reason: z.literal('low_accuracy'),
  /**
   * Position of the word in its sentence (0-based), as in DB v0.2
   * `word_flags.token_index`. Together with the segment `sequence` it gives
   * each flag its provenance and orders ties by position in the reading.
   */
  token_index: z.number().int().min(0),
})
export type WordFlag = z.infer<typeof wordFlagSchema>

/**
 * Normalized FR-74 record for one recorded sentence segment. In this contract
 * `sequence` refers to the authored sentence of the delivered item version
 * (`item_id` + `sequence`). The server-side session segment (DB v0.2
 * `read_aloud_segments`, one row per session and sequence) is a different
 * identity; see the authoring schema §6 and open items O-11 and O-13. Each
 * segment is assessed, timed out (FR-78: 15 s) and de-duplicated
 * (`client_attempt_id` + sequence) on its own.
 */
export const readAloudSegmentAssessmentSchema = z
  .strictObject({
    sequence: segmentSequence,
    assessment_status: assessmentStatusSchema,
    audio_quality: audioQualitySchema,
    word_flags: z.array(wordFlagSchema).max(MAX_FLAGGED_WORDS_LIMIT),
  })
  .superRefine(readAloudStatusIssues)
  .refine((s) => !hasDuplicates(s.word_flags.map((f) => f.token_index)), {
    message: 'one word flag per word position',
  })
export type ReadAloudSegmentAssessment = z.infer<typeof readAloudSegmentAssessmentSchema>

/**
 * Normalized assessment of one read-aloud item — the single source of truth,
 * built server-side from Azure Pronunciation Assessment output. One entry per
 * *submitted* segment; a sentence that was not recorded has no entry and is
 * never treated as a pronunciation error. There is deliberately no item-level
 * status or score. Not child-facing: it carries accuracy (FR-76).
 */
export const readAloudAssessmentSchema = z
  .strictObject({
    segments: z.array(readAloudSegmentAssessmentSchema).min(1),
  })
  .refine((a) => !hasDuplicates(a.segments.map((s) => s.sequence)), {
    message: 'one assessment per segment',
  })
export type ReadAloudAssessment = z.infer<typeof readAloudAssessmentSchema>

/** Problems with an assessment under a given FR-75 configuration. */
export function readAloudAssessmentProblems(
  assessment: ReadAloudAssessment,
  config: ReadAloudConfig,
): string[] {
  const problems: string[] = []
  for (const segment of assessment.segments) {
    if (segment.word_flags.length > config.max_flagged_words) {
      problems.push(
        `segment ${String(segment.sequence)}: more word flags than max_flagged_words`,
      )
    }
    if (segment.word_flags.some((f) => f.accuracy >= config.low_accuracy_threshold)) {
      problems.push(
        `segment ${String(segment.sequence)}: word flag accuracy is not below low_accuracy_threshold`,
      )
    }
  }
  return problems
}

/** Child-facing word flag (FR-76): the word to practise, never its score. */
export const childWordFlagSchema = z.strictObject({
  word: text,
  reason: z.literal('low_accuracy'),
})

export const childReadAloudSegmentSchema = z
  .strictObject({
    sequence: segmentSequence,
    assessment_status: assessmentStatusSchema,
    audio_quality: audioQualitySchema,
    word_flags: z.array(childWordFlagSchema).max(MAX_FLAGGED_WORDS_LIMIT),
  })
  .superRefine(readAloudStatusIssues)

/**
 * The word flags the child sees for a whole reading (decision O-10): at most
 * `max_flagged_words`, lowest accuracy first; equal accuracy goes to the word
 * that appears earlier in the reading (lower segment `sequence`, then lower
 * `token_index`). Returns the chosen flags with their segment provenance. The
 * stored assessment is not modified — flags that are not shown stay stored.
 */
export function selectChildWordFlags(
  assessment: ReadAloudAssessment,
  config: ReadAloudConfig,
): { sequence: number; flag: WordFlag }[] {
  return assessment.segments
    .flatMap((s) => s.word_flags.map((flag) => ({ sequence: s.sequence, flag })))
    .sort(
      (a, b) =>
        a.flag.accuracy - b.flag.accuracy ||
        a.sequence - b.sequence ||
        a.flag.token_index - b.flag.token_index,
    )
    .slice(0, config.max_flagged_words)
}

/**
 * Child-facing projection (FR-76, FR-77): per-segment status and quality, the
 * words chosen by `selectChildWordFlags` without accuracy (each listed under
 * its own segment, in reading order), and `practice_recorded`.
 */
export function toChildReadAloud(
  assessment: ReadAloudAssessment,
  config: ReadAloudConfig,
) {
  const shown = selectChildWordFlags(assessment, config)
  return {
    segments: assessment.segments.map((s) => ({
      sequence: s.sequence,
      assessment_status: s.assessment_status,
      audio_quality: s.audio_quality,
      word_flags: shown
        .filter((x) => x.sequence === s.sequence)
        .map((x) => x.flag)
        .sort((a, b) => a.token_index - b.token_index)
        .map((flag) => ({ word: flag.word, reason: flag.reason })),
    })),
    practice_recorded: true as const,
  }
}

// ---------------------------------------------------------------------------
// Results (assessment service → client)
// ---------------------------------------------------------------------------

/** FR-146: short, non-shaming. Answers are revealed only after submission. */
export const feedbackSchema = z.strictObject({
  message_en: text,
  explanation_vi: text.nullable(),
  correct_answer_text: text.nullable(),
})
export type Feedback = z.infer<typeof feedbackSchema>

/** A result belongs to one item version and one submission attempt. */
const resultBase = {
  item_id: z.uuid(),
  client_attempt_id: z.uuid(),
}

/** Item types whose `assessment_rules` may award partial credit (D-11). */
export const PARTIAL_CREDIT_TYPES: readonly ActivityType[] = ['matching', 'gap_fill']

/**
 * The stored outcome of one item submission. Persistent and replayable: it
 * carries no per-call metadata (see `SubmitItemResponse`).
 */
export const itemResultSchema = z.discriminatedUnion('outcome', [
  /**
   * Objective item scored by the backend (FR-50), using the item's
   * `assessment_rules`. The client displays these values; it never computes
   * them. `is_correct` is true only for a full score (D-11).
   */
  z
    .strictObject({
      ...resultBase,
      outcome: z.literal('graded'),
      is_correct: z.boolean(),
      score: z.number().min(0),
      max_score: z.number().positive(),
      feedback: feedbackSchema,
    })
    .refine((r) => r.score <= r.max_score, { message: 'score exceeds max_score' })
    .refine((r) => r.is_correct === (r.score === r.max_score), {
      message: 'is_correct must match a full score',
    }),
  /** Diagnostic/assessment: response stored, result withheld until the end (FR-42). */
  z.strictObject({
    ...resultBase,
    outcome: z.literal('recorded'),
  }),
  /** Read aloud, child-facing projection of `ReadAloudAssessment` (FR-74–FR-77). */
  z
    .strictObject({
      ...resultBase,
      outcome: z.literal('read_aloud'),
      segments: z.array(childReadAloudSegmentSchema).min(1),
      practice_recorded: z.literal(true),
    })
    .refine((r) => !hasDuplicates(r.segments.map((s) => s.sequence)), {
      message: 'one result per segment',
    })
    .refine(
      (r) =>
        r.segments.reduce((n, s) => n + s.word_flags.length, 0) <=
        MAX_FLAGGED_WORDS_LIMIT,
      { message: 'the child sees at most max_flagged_words words per reading (FR-76)' },
    ),
  /** Speaking prompt (decision D-03): completion only — no score, no correctness. */
  z.strictObject({
    ...resultBase,
    outcome: z.literal('completed'),
  }),
])
export type ItemResult = z.infer<typeof itemResultSchema>

/**
 * What one `submitItem` call returns. `idempotent_replay` describes this call
 * (FR-41: the same attempt was already stored), not the result itself, so it
 * never appears in stored results or session summaries.
 */
export const submitItemResponseSchema = z.strictObject({
  idempotent_replay: z.boolean(),
  result: itemResultSchema,
})
export type SubmitItemResponse = z.infer<typeof submitItemResponseSchema>

/**
 * Per-attempt observation for one learning objective
 * (first-learning-journey §4). Calculated by the backend from this attempt's
 * responses; the client displays it and never recalculates it. It is not a
 * competency, mastery or weakness state.
 */
export const OBJECTIVE_STATUSES = [
  'insufficient_evidence',
  'needs_practice',
  'observed_correct',
] as const
export const objectiveStatusSchema = z.enum(OBJECTIVE_STATUSES)
export type ObjectiveStatus = z.infer<typeof objectiveStatusSchema>

export const objectiveObservationSchema = z
  .strictObject({
    /** Opaque objective code owned by the domain module. */
    code: text,
    label_vi: text,
    attempted: z.number().int().min(0),
    correct: z.number().int().min(0),
    status: objectiveStatusSchema,
  })
  .refine((o) => o.correct <= o.attempted, {
    message: 'correct answers cannot exceed attempted answers',
  })
export type ObjectiveObservation = z.infer<typeof objectiveObservationSchema>

/** A pre-authored practice lesson for one objective that needs practice. */
export const recommendedPracticeSchema = lessonSummarySchema.extend({
  objective_code: text,
})
export type RecommendedPractice = z.infer<typeof recommendedPracticeSchema>

/**
 * Truthful session facts only (UI v0.2 §4.5): no mastery, level or
 * proficiency claims. `graded` counts the `graded` results and is null when
 * there are none, so partial and non-objective sessions stay valid.
 */
export const sessionSummarySchema = z
  .strictObject({
    session_id: z.uuid(),
    status: z.literal('completed'),
    items_total: z.number().int().min(1),
    items_submitted: z.number().int().min(0),
    graded: z
      .strictObject({
        correct: z.number().int().min(0),
        total: z.number().int().min(1),
      })
      .nullable(),
    /**
     * One result per submitted item (each item is submitted once while the
     * FR-161 retry policy is open); for diagnostic/assessment this is where
     * scores appear.
     */
    results: z.array(itemResultSchema),
    is_mock: z.boolean(),
    /** Objective observations for this attempt only, in lesson order. */
    objectives: z.array(objectiveObservationSchema),
    /**
     * Supplementary practice for objectives that need practice; empty after a
     * practice lesson and whenever nothing needs practice.
     */
    recommended_practice: z.array(recommendedPracticeSchema),
  })
  .superRefine((s, ctx) => {
    const issue = (message: string) => {
      ctx.addIssue({ code: 'custom', message })
    }
    if (s.items_submitted > s.items_total) issue('items_submitted exceeds items_total')
    if (s.results.length !== s.items_submitted) {
      issue('results must contain one entry per submitted item')
    }
    if (hasDuplicates(s.results.map((r) => r.item_id)))
      issue('duplicate result for an item')
    if (hasDuplicates(s.results.map((r) => r.client_attempt_id))) {
      issue('duplicate result for an attempt')
    }
    const graded = s.results.flatMap((r) => (r.outcome === 'graded' ? [r] : []))
    if (graded.length === 0) {
      if (s.graded !== null) issue('graded must be null when nothing was graded')
    } else if (
      s.graded?.total !== graded.length ||
      s.graded.correct !== graded.filter((r) => r.is_correct).length
    ) {
      issue('graded counts must match the graded results')
    }
    if (hasDuplicates(s.objectives.map((o) => o.code))) issue('duplicate objective')
    if (hasDuplicates(s.recommended_practice.map((p) => p.lesson_id))) {
      issue('duplicate recommended lesson')
    }
    const needsPractice = s.objectives
      .filter((o) => o.status === 'needs_practice')
      .map((o) => o.code)
    if (!s.recommended_practice.every((p) => needsPractice.includes(p.objective_code))) {
      issue('practice is recommended only for objectives that need practice')
    }
  })
export type SessionSummary = z.infer<typeof sessionSummarySchema>

const OUTCOMES_BY_TYPE: Readonly<Record<ActivityType, readonly ItemResult['outcome'][]>> =
  {
    text_choice: ['graded', 'recorded'],
    picture_choice: ['graded', 'recorded'],
    matching: ['graded', 'recorded'],
    word_order: ['graded', 'recorded'],
    gap_fill: ['graded', 'recorded'],
    listen_respond: ['graded', 'recorded'],
    read_and_answer: ['graded', 'recorded'],
    read_aloud: ['read_aloud'],
    speaking_prompt: ['completed'],
  }

/** Does an item result fit the delivered item, regardless of timing? */
export function resultProblems(item: ActivityItem, result: ItemResult): string[] {
  if (result.item_id !== item.item_id) return ['result is for a different item']
  if (!OUTCOMES_BY_TYPE[item.activity_type].includes(result.outcome)) {
    return [`${result.outcome} result does not fit a ${item.activity_type} item`]
  }
  if (
    result.outcome === 'graded' &&
    result.score > 0 &&
    result.score < result.max_score &&
    !PARTIAL_CREDIT_TYPES.includes(item.activity_type)
  ) {
    return [`partial credit is not defined for ${item.activity_type}`]
  }
  if (result.outcome === 'read_aloud' && item.activity_type === 'read_aloud') {
    const delivered = item.sentences.map((s) => s.sequence)
    if (!result.segments.every((s) => delivered.includes(s.sequence))) {
      return ['read-aloud result for a sentence that was not delivered']
    }
  }
  return []
}

/**
 * Does the result returned for one submission respect FR-42 timing? Practice
 * gets objective feedback immediately; diagnostic and assessment sessions must
 * not reveal scores or answers before the session ends.
 */
export function submissionResultProblems(
  item: ActivityItem,
  result: ItemResult,
  dataClass: DataClass,
): string[] {
  const problems = resultProblems(item, result)
  if (!feedbackIsImmediate(dataClass) && result.outcome === 'graded') {
    problems.push(
      'graded feedback is withheld until the end of a diagnostic/assessment session',
    )
  }
  if (feedbackIsImmediate(dataClass) && result.outcome === 'recorded') {
    problems.push('practice results are returned immediately')
  }
  return problems
}

/** Does a session summary fit the session it claims to summarize? */
export function summaryProblems(
  delivery: SessionDelivery,
  summary: SessionSummary,
): string[] {
  const problems: string[] = []
  if (summary.session_id !== delivery.session_id)
    problems.push('summary is for another session')
  if (summary.items_total !== delivery.items.length) {
    problems.push('items_total does not match the session')
  }
  if (
    !feedbackIsImmediate(delivery.data_class) &&
    summary.results.some((r) => r.outcome === 'recorded')
  ) {
    problems.push('final diagnostic/assessment results must be scored')
  }
  for (const result of summary.results) {
    const item = delivery.items.find((i) => i.item_id === result.item_id)
    if (item === undefined) problems.push(`result for unknown item ${result.item_id}`)
    else problems.push(...resultProblems(item, result))
  }
  return problems
}
